import { useEffect, useState } from "react";
import { Search, Loader2, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/* Componentes compartilhados da Central Administrativa. */

export function PageHeader({ icon: Icon, title, description, actions, testId }) {
  return (
    <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-testid={testId}>
      <div className="min-w-0">
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          {Icon && <Icon size={24} className="text-primary shrink-0" aria-hidden="true" />}
          <span className="truncate">{title}</span>
        </h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function StatTile({ label, value, hint, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-2xl font-bold">{value ?? "—"}</p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder = "Pesquisar…", testId, delay = 250 }) {
  const [text, setText] = useState(value || "");
  useEffect(() => setText(value || ""), [value]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (text !== (value || "")) onChange(text);
    }, delay);
    return () => clearTimeout(timer);
  }, [text, value, onChange, delay]);
  return (
    <div className="relative w-full sm:max-w-xs">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <Input
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="pl-9"
        data-testid={testId}
      />
    </div>
  );
}

export function LoadingState({ label = "Carregando…" }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground" role="status">
      <Loader2 size={16} className="animate-spin" aria-hidden="true" /> {label}
    </div>
  );
}

export function EmptyState({ title, description, action, testId }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid={testId}>
      <Inbox size={28} className="text-muted-foreground" aria-hidden="true" />
      <p className="text-sm font-medium">{title}</p>
      {description && <p className="max-w-md text-xs text-muted-foreground">{description}</p>}
      {action}
    </div>
  );
}

const PILL_STYLES = {
  success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  warning: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
  danger: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30",
  muted: "bg-muted text-muted-foreground border-border",
  info: "bg-primary/10 text-primary border-primary/30",
};

export function StatePill({ tone = "muted", children, testId }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${PILL_STYLES[tone] || PILL_STYLES.muted}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

/** Confirmação com justificativa obrigatória (suspender, desabilitar, restaurar…). */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirmar",
  destructive = false,
  minLength = 5,
  requireReason = true,
  optional = false,
  label,
  placeholder,
  onConfirm,
  testId = "reason-dialog",
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setReason("");
  }, [open]);
  const valid = !requireReason || optional || reason.trim().length >= minLength;
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {requireReason && (
          <div className="space-y-1.5">
            <label htmlFor={`${testId}-reason`} className="text-xs font-semibold text-muted-foreground">
              {label || (optional ? "Observação (opcional)" : `Justificativa (mínimo ${minLength} caracteres)`)}
            </label>
            <Textarea
              id={`${testId}-reason`}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              placeholder={placeholder}
              data-testid={`${testId}-input`}
            />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={confirm}
            disabled={!valid || busy}
            data-testid={`${testId}-confirm`}
          >
            {busy && <Loader2 size={14} className="mr-2 animate-spin" />} {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Barra fixa de alterações pendentes (evita perda e deixa claro o que falta salvar). */
export function UnsavedBar({ count, onSave, onDiscard, saving, testId = "unsaved-bar" }) {
  if (!count) return null;
  return (
    <div
      className="sticky bottom-3 z-20 mx-auto flex w-full max-w-3xl items-center justify-between gap-3 rounded-xl border border-primary/30 bg-card/95 px-4 py-3 shadow-lg backdrop-blur"
      role="status"
      data-testid={testId}
    >
      <p className="text-sm">
        <strong>{count}</strong> alteração(ões) não salva(s)
      </p>
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={onDiscard} disabled={saving} data-testid={`${testId}-discard`}>
          Descartar
        </Button>
        <Button size="sm" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}>
          {saving && <Loader2 size={14} className="mr-2 animate-spin" />} Salvar
        </Button>
      </div>
    </div>
  );
}

/** Mensagem de erro da API (detalhe string, objeto com message ou lista). */
export function apiErrorMessage(error, fallback = "Não foi possível concluir a operação.") {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (detail.message) return detail.modules ? `${detail.message} (${detail.modules.join(", ")})` : detail.message;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || String(item)).join("; ");
  return fallback;
}
